import React,{useState,useRef,useCallback,useEffect} from 'react';
import {PaywallScreen} from '../apps/AuthScreens';
import {TeamChatView} from '../components/Chat';
import {PlayerLeaguesView} from '../components/Leagues';
import {CoachPlayerStatsView,ReadOnlyPitch} from '../components/Match';
import {MyPaymentsView} from '../components/Payments';
import {AccessibilitySettingsPanel,AchievementBadges,Flag,HomeAwaySplitCard,MatchCalendarView,MatchKeyInfo,NotificationSettingsPanel,PitchBackdrop,StatsFilterMenu,useStatsVisibility} from '../components/Shared';
import {api} from '../lib/api';
import {LOGO_ICON_DATA_URI} from '../lib/assets';
import {STATUS_COLORS,STATUS_OPTIONS} from '../lib/constants';
import {Icon} from '../components/Icons';
import {computeEntitlements,fmtDate,modalPopSuppress} from '../lib/utils';

export function ParentApp({auth,onSignOut}){
  const [team,setTeam]=useState(null);
  const [child,setChild]=useState(null);
  // Whether there's actually a payment plan this parent is allowed to see for the
  // current team — RLS only lets a parent SELECT a payment_plans row when the coach
  // has switched "Visible to Parents" on for it, so an empty result here means either
  // there's no plan yet or it's specifically been kept hidden from parents. Either
  // way, the Payments tab shouldn't show at all rather than leading to an empty page.
  // null (not false) while it's still unknown — team/child themselves load
  // asynchronously, so a plain false here at mount would look identical to "really has
  // no payments" and could bounce someone straight off a Payments deep link (e.g.
  // returning from Stripe Checkout) before their team's even finished loading.
  const [paymentsVisible,setPaymentsVisible]=useState(null);
  useEffect(()=>{
    if(!team?.id){setPaymentsVisible(false);return;}
    let cancelled=false;
    api.get(auth.token,"payment_plans",`team_id=eq.${team.id}&select=id`).then(rows=>{
      if(!cancelled)setPaymentsVisible(!!(rows&&rows.length>0));
    });
    return()=>{cancelled=true;};
  },[team?.id]);
  const [players,setPlayers]=useState([]);
  const [fixtures,setFixtures]=useState([]);
  const [sessions,setSessions]=useState([]);
  const [teamStrategies,setTeamStrategies]=useState([]);
  const [teamAchievementRows,setTeamAchievementRows]=useState([]);
  const [loading,setLoading]=useState(true);
  // Land straight back on Payments if we're returning from a Stripe Checkout redirect
  // (?payment=success/cancelled in the URL) rather than defaulting to Matches — the
  // whole point of coming back is to see the payment confirmation, not to have to
  // find the tab again after a login that (with session persistence) shouldn't even
  // be necessary anymore.
  const [view,setView]=useState(()=>new URLSearchParams(window.location.search).get("payment")?"payments":"matches"); // matches | stats | chat | leagues
  // Safety net: if the Payments tab was open and then becomes unavailable (switched
  // child/team, or the coach turned "Visible to Parents" off), bounce back to Matches
  // rather than leaving the parent stuck on a tab that's no longer in the nav bar. Only
  // fires once paymentsVisible has actually resolved (not while it's still null) and
  // the initial team/child load has finished, so a Payments deep link survives the
  // brief window before those finish loading.
  useEffect(()=>{
    if(view==="payments"&&!loading&&paymentsVisible===false)setView("matches");
  },[view,loading,paymentsVisible]);
  const [orgOpenToLeagues,setOrgOpenToLeagues]=useState(false);
  const [orgBilling,setOrgBilling]=useState(null); // {name,trial_ends_at,subscription_active} — for the trial paywall
  const [statsTab,setStatsTab]=useState("team"); // team | player
  // Which Team Stats cards are currently visible — defaults to "All" on every page load.
  const [teamStatsVisible,setTeamStatsVisible]=useStatsVisibility([{id:"quickStats"},{id:"resultsBreakdown"},{id:"homeAway"},{id:"matchResults"},{id:"topScorers"},{id:"topAssisters"},{id:"squadAvailability"},{id:"teamAchievements"}]);
  const [detailMatch,setDetailMatch]=useState(null);
  const [matchTab,setMatchTab]=useState("result");
  const [matchesViewMode,setMatchesViewMode]=useState("list"); // "list" | "calendar"
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<640);
  useEffect(()=>{const h=()=>setIsMobile(window.innerWidth<640);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  // Tablet (e.g. iPad) widths keep the desktop layout, but the top tab row gets too
  // cramped to fit — so from this width down, switch just the navigation to the same
  // bottom nav bar mobile uses.
  const [isBottomNav,setIsBottomNav]=useState(()=>window.innerWidth<1024);
  useEffect(()=>{const h=()=>setIsBottomNav(window.innerWidth<1024);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  // Safety net: this app doesn't use the browser history for its tabs, so a back press
  // would otherwise fall straight through to whatever real browser history existed
  // before the app loaded — which can look like (or actually cause) a silent logout.
  // Trap it behind a floor entry instead, and only offer to log out if they confirm.
  const [showLogoutConfirm,setShowLogoutConfirm]=useState(false);
  useEffect(()=>{
    window.history.pushState({__floor:true},"");
    function onPop(e){
      if(modalPopSuppress.active>0){ modalPopSuppress.active--; return; }
      const s=e.state;
      window.history.pushState({__floor:true},"");
      if(!s||!s.__floor)return; // shouldn't happen, but never surface a real navigation
      setShowLogoutConfirm(true);
    }
    window.addEventListener("popstate",onPop);
    return ()=>window.removeEventListener("popstate",onPop);
  },[]);
  const ac="#8b5cf6";
  const AS={accentColor:ac,bgColor:"var(--c-bg0)",surfaceColor:"var(--c-surface)",borderColor:"var(--c-border)"};
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};

  // ── Multi-child support ──
  const [children,setChildren]=useState(null); // [{player_id,team_id,org_id,player:{name,...}}]
  const [selectedChildId,setSelectedChildId]=useState(null); // a player_id
  const [showAddChild,setShowAddChild]=useState(false);
  const [addCode,setAddCode]=useState("");
  const [addingChild,setAddingChild]=useState(false);
  const [addChildErr,setAddChildErr]=useState("");

  useEffect(()=>{ loadChildren(); },[]);
  async function loadChildren(){
    const rows=await api.get(auth.token,"parent_children",`parent_viewer_id=eq.${auth.viewer.id}&order=created_at.asc`);
    if(rows&&rows.length>0){
      const pids=rows.map(r=>r.player_id).join(",");
      const ps=await api.get(auth.token,"players",`id=in.(${pids})`);
      const merged=rows.map(r=>({...r,player:(ps||[]).find(p=>p.id===r.player_id)}));
      setChildren(merged);
      setSelectedChildId(prev=>prev||rows[0].player_id);
    } else {
      // Accounts created before multi-child support — fall back to the single child on the login row.
      setChildren([{player_id:auth.viewer.player_id,team_id:auth.viewer.team_id,org_id:auth.viewer.org_id}]);
      setSelectedChildId(auth.viewer.player_id);
    }
  }
  async function addChild(){
    setAddingChild(true);setAddChildErr("");
    const codeTrim=addCode.trim().toUpperCase();
    if(!codeTrim){setAddChildErr("Enter a join code");setAddingChild(false);return;}
    const kids=await api.get(auth.token,"players",`parent_join_code=eq.${codeTrim}&select=id,org_id,name`);
    if(!kids||kids.length===0){setAddChildErr("Invalid join code — check with your child's coach");setAddingChild(false);return;}
    if((children||[]).some(c=>c.player_id===kids[0].id)){setAddChildErr("This child is already linked to your account");setAddingChild(false);return;}
    const teamRows=await api.get(auth.token,"team_players",`player_id=eq.${kids[0].id}&select=team_id`);
    const teamId=teamRows&&teamRows[0]?teamRows[0].team_id:null;
    if(!teamId){setAddChildErr("This player isn't assigned to a team yet");setAddingChild(false);return;}
    const created=await api.post(auth.token,"parent_children",[{parent_viewer_id:auth.viewer.id,player_id:kids[0].id,team_id:teamId,org_id:kids[0].org_id}]);
    if(created&&created[0]){
      setAddCode("");setShowAddChild(false);
      await loadChildren();
      setSelectedChildId(kids[0].id);
    } else {
      setAddChildErr("Something went wrong — please try again");
    }
    setAddingChild(false);
  }

  const activeChild=(children||[]).find(c=>c.player_id===selectedChildId);

  useEffect(()=>{ if(activeChild)load(); },[activeChild?.team_id,activeChild?.player_id]);
  useEffect(()=>{
    if(activeChild?.org_id)api.get(auth.token,"organisations",`id=eq.${activeChild.org_id}&select=name,open_to_leagues,trial_ends_at,subscription_active,plan_tier,billing_cycle,parent_addon,player_addon,feature_achievements,feature_player_development,pitch_color_1,pitch_color_2,pitch_brightness,pitch_match_halves,pitch_stripes,feature_player_ratings,feature_leagues,feature_payments,stripe_charges_enabled`).then(rows=>{
      setOrgOpenToLeagues(!!(rows&&rows[0]&&rows[0].open_to_leagues));
      setOrgBilling(rows&&rows[0]||null);
    });
  },[activeChild?.org_id]);
  const [allSeasons,setAllSeasons]=useState([]);
  const [viewSeasonId,setViewSeasonId]=useState(null); // null = current/live season
  useEffect(()=>{
    if(activeChild?.org_id)api.get(auth.token,"seasons",`org_id=eq.${activeChild.org_id}&order=created_at.desc`).then(rows=>setAllSeasons(rows||[]));
  },[activeChild?.org_id]);
  async function switchSeason(seasonId){
    setViewSeasonId(seasonId);
    if(!seasonId){await load();return;}
    if(!activeChild?.player_id){return;}
    const tps=await api.get(auth.token,"team_players",`player_id=eq.${activeChild.player_id}&select=team_id`);
    const teamIds=(tps||[]).map(t=>t.team_id);
    if(teamIds.length===0){setTeam(null);setPlayers([]);setFixtures([]);setSessions([]);return;}
    const teamsInSeason=await api.get(auth.token,"teams",`id=in.(${teamIds.join(",")})&season_id=eq.${seasonId}&select=id`);
    const foundTeamId=teamsInSeason&&teamsInSeason[0]?teamsInSeason[0].id:null;
    if(!foundTeamId){setTeam(null);setPlayers([]);setFixtures([]);setSessions([]);return;}
    await load(foundTeamId);
  }
  async function load(teamIdOverride){
    setLoading(true);
    const teamId=teamIdOverride||activeChild.team_id, playerId=activeChild.player_id;
    const t=await api.get(auth.token,"teams",`id=eq.${teamId}`);
    if(t&&t[0])setTeam(t[0]);
    const c=await api.get(auth.token,"players",`id=eq.${playerId}`);
    if(c&&c[0])setChild(c[0]);
    const tps=await api.get(auth.token,"team_players",`team_id=eq.${teamId}`);
    const pids=(tps||[]).map(tp=>tp.player_id);
    if(pids.length>0){
      // Squad-wide fetch: explicit columns, so other people's children's dates of
      // birth never reach this browser. A parent has no reason to hold personal data
      // about the rest of the squad, and hiding a field in the UI does nothing while
      // the raw row is still sitting in the network tab. Their own child's full row —
      // DOB included — is loaded separately above via id=eq.${playerId}, which is the
      // one player whose details they are entitled to.
      const ps=await api.get(auth.token,"players",`id=in.(${pids.join(",")})&select=id,name,number,position,nationality,photo_url`);
      setPlayers((ps||[]).map(p=>{
        const tp=(tps||[]).find(x=>x.player_id===p.id);
        return {...p,goals:tp?.goals||0,assists:tp?.assists||0,status:tp?.status||"Available"};
      }));
    } else setPlayers([]);
    const fs=await api.get(auth.token,"fixtures",`team_id=eq.${teamId}&order=date.desc`);
    setFixtures(fs||[]);
    const ss=await api.get(auth.token,"sessions",`team_id=eq.${teamId}&order=date.desc`);
    if(ss&&ss.length>0){
      const sids=ss.map(s=>s.id).join(",");
      const att=await api.get(auth.token,"attendance",`session_id=in.(${sids})`);
      setSessions(ss.map(s=>({...s,attendance:Object.fromEntries((att||[]).filter(a=>a.session_id===s.id).map(a=>[a.player_id,a.status]))})));
    } else setSessions([]);
    const strats=await api.get(auth.token,"team_strategies",`team_id=eq.${teamId}&order=created_at.asc`);
    setTeamStrategies(strats||[]);
    const achv=await api.get(auth.token,"team_achievements",`team_id=eq.${teamId}`);
    setTeamAchievementRows(achv||[]);
    setLoading(false);
  }
  function onPlayerPhotoUpdated(playerId,url){
    setPlayers(prev=>prev.map(p=>p.id===playerId?{...p,photo_url:url}:p));
    if(child?.id===playerId)setChild(c=>({...c,photo_url:url}));
  }
  function switchChild(playerId){
    setSelectedChildId(playerId);
    setDetailMatch(null);setView("matches");setStatsTab("team");
  }

  if(children===null||loading)return <div style={{minHeight:"100vh",background:AS.bgColor,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--c-muted)",fontSize:13}}>Loading…</div>;

  if(orgBilling&&computeEntitlements(orgBilling).status==="expired")return <PaywallScreen org={orgBilling} isHead={false} onSignOut={onSignOut}/>;

  const featAchievements=orgBilling?.feature_achievements!==false;
  const featPlayerDev=orgBilling?.feature_player_development!==false;
  const featPlayerRatings=orgBilling?.feature_player_ratings!==false;
  const featMatchDay=!!orgBilling?.feature_match_day;
  const featLeagues=orgBilling?.feature_leagues!==false;
  const featPayments=orgBilling?.feature_payments!==false;

  const playedMatches=fixtures.filter(f=>!f.matches&&f.played);
  const upcoming=fixtures.filter(f=>!f.played).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const topScorer=[...players].sort((a,b)=>(b.goals||0)-(a.goals||0))[0];
  // Same [key,icon,label] shape PlayerApp/MainApp use for their nav — keeps the mobile
  // bottom nav and desktop tab row consistent with the rest of the app instead of the
  // wrapping, fixed-width tab row this page used to have of its own.
  const navTabs=[["matches","calendar","Matches"],["stats","chart","Stats"],...(featLeagues?[["leagues","trophy","Leagues"]]:[]),...(featPayments&&paymentsVisible?[["payments","note","Payments"]]:[]),["chat","chat","Chat"],["notifications","bell","Alerts"],["accessibility","access","Accessibility"]];

  return(
    <div style={{minHeight:"100vh",background:"transparent",paddingBottom:isBottomNav?"calc(65px + env(safe-area-inset-bottom,0px))":0,position:"relative"}}>
      <PitchBackdrop color1={orgBilling?.pitch_color_1} color2={orgBilling?.pitch_color_2} brightness={orgBilling?.pitch_brightness} matchHalves={orgBilling?.pitch_match_halves} stripes={orgBilling?.pitch_stripes}/>
      <div style={{position:"sticky",top:0,zIndex:120,background:"rgba(255,255,255,0.94)",backdropFilter:"blur(8px)",borderBottom:`1px solid ${AS.borderColor}`,padding:"12px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:9}}>
          <img src={LOGO_ICON_DATA_URI} alt="pitchMGR" style={{width:isMobile?24:28,height:isMobile?24:28,borderRadius:6,flexShrink:0}}/>
          <div>
            <div style={{fontSize:15,fontWeight:800}}>{team?.name||"pitchMGR"}</div>
            <div style={{fontSize:11,color:"var(--c-muted)",marginTop:1}}>Parent of {child?.name||"—"}</div>
          </div>
        </div>
        {/* On desktop the nav lives in the left sidebar now (see below) instead of
            crowding this top bar — this row just keeps identity/status/actions. */}
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {allSeasons.length>1&&(
            <select value={viewSeasonId||""} onChange={e=>switchSeason(e.target.value||null)} style={{padding:"6px 9px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text2)",fontSize:11,cursor:"pointer"}}>
              <option value="">Current Season</option>
              {allSeasons.filter(s=>!s.is_current).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <button onClick={onSignOut} style={{background:"none",border:`1px solid ${AS.borderColor}`,borderRadius:7,color:"var(--c-muted)",cursor:"pointer",padding:"6px 11px",fontSize:11}}>Sign Out</button>
        </div>
      </div>

      <div style={{position:"relative",zIndex:1,maxWidth:isBottomNav?900:1120,margin:"16px auto",display:isBottomNav?"block":"flex",alignItems:"flex-start",gap:16,padding:isBottomNav?undefined:"0 14px"}}>
      {/* Desktop sidebar nav — on mobile/tablet widths (isBottomNav) the same tabs show
          in the bottom nav bar instead (see below), so this is skipped there. */}
      {!isBottomNav&&(
        <nav style={{width:190,flexShrink:0,position:"sticky",top:71,display:"flex",flexDirection:"column",gap:3,background:"rgba(255,255,255,0.93)",border:`1px solid ${AS.borderColor}`,borderRadius:14,padding:10,boxShadow:"0 10px 40px rgba(20,83,45,0.1)"}}>
          {navTabs.map(([v,icon,label])=>(
            <button key={v} onClick={()=>{setView(v);setDetailMatch(null);}} style={{display:"flex",alignItems:"center",gap:9,padding:"9px 11px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,textAlign:"left",background:view===v?ac:"transparent",color:view===v?"#fff":"var(--c-text2)"}}>
              <Icon name={icon} size={15}/>{label}
            </button>
          ))}
        </nav>
      )}
      <div style={{flex:1,minWidth:0,maxWidth:isBottomNav?900:undefined,margin:isBottomNav?"0 auto":undefined,padding:isMobile?"10px 10px 16px":"16px",background:"rgba(255,255,255,0.93)",borderRadius:18,boxShadow:"0 10px 40px rgba(20,83,45,0.12)"}}>
        {viewSeasonId&&(
          <div style={{background:"#fef3c7",border:"1px solid #f59e0b55",borderRadius:9,padding:"9px 13px",marginBottom:14,fontSize:11,color:"#92400e",fontWeight:600}}>
            📁 Viewing {allSeasons.find(s=>s.id===viewSeasonId)?.name||"a past season"}{!team&&` — ${child?.name||"your child"} wasn't on a team that season.`}
          </div>
        )}
        {((children||[]).length>1||showAddChild)&&(
          <div style={{...C,padding:12,marginBottom:14}}>
            {(children||[]).length>1&&(
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:showAddChild?10:0}}>
                {children.map(c=>(
                  <button key={c.player_id} onClick={()=>switchChild(c.player_id)} style={{padding:"7px 13px",borderRadius:7,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,background:c.player_id===selectedChildId?ac:AS.bgColor,color:c.player_id===selectedChildId?"#fff":"var(--c-muted)"}}>{c.player?.name||"Child"}</button>
                ))}
                {!showAddChild&&<button onClick={()=>setShowAddChild(true)} style={{padding:"7px 13px",borderRadius:7,border:`1px dashed ${AS.borderColor}`,background:"none",cursor:"pointer",fontSize:12,fontWeight:700,color:"var(--c-muted)"}}>+ Add Child</button>}
              </div>
            )}
            {(children||[]).length===1&&!showAddChild&&(
              <button onClick={()=>setShowAddChild(true)} style={{padding:"7px 13px",borderRadius:7,border:`1px dashed ${AS.borderColor}`,background:"none",cursor:"pointer",fontSize:12,fontWeight:700,color:"var(--c-muted)"}}>+ Add Another Child</button>
            )}
            {showAddChild&&(
              <div style={{display:"flex",gap:8,alignItems:"flex-start",flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:160}}>
                  <input value={addCode} onChange={e=>setAddCode(e.target.value.toUpperCase())} placeholder="ABC123" maxLength={8} style={{width:"100%",padding:"8px 10px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:14,fontWeight:700,letterSpacing:"0.08em",textAlign:"center",boxSizing:"border-box"}}/>
                  {addChildErr&&<div style={{fontSize:10,color:"#ef4444",marginTop:4}}>{addChildErr}</div>}
                </div>
                <button onClick={addChild} disabled={addingChild} style={{padding:"8px 16px",borderRadius:7,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer",opacity:addingChild?0.7:1}}>{addingChild?"Adding…":"Add"}</button>
                <button onClick={()=>{setShowAddChild(false);setAddCode("");setAddChildErr("");}} style={{padding:"8px 12px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:12,cursor:"pointer"}}>Cancel</button>
              </div>
            )}
          </div>
        )}
        {view==="matches"&&!detailMatch&&(
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{display:"flex",justifyContent:"flex-end"}}>
              <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:7,padding:3}}>
                {[["list","☰ List"],["calendar","📅 Calendar"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setMatchesViewMode(v)} style={{padding:"6px 11px",borderRadius:5,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:matchesViewMode===v?ac:"transparent",color:matchesViewMode===v?"#fff":"var(--c-muted)"}}>{l}</button>
                ))}
              </div>
            </div>
            {matchesViewMode==="list"?(<>
            {upcoming.length>0&&<div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Upcoming</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",gap:9}}>
                {upcoming.map(m=>(
                  <div key={m.id} onClick={()=>{setDetailMatch(m);setMatchTab("lineup");}} style={{...C,padding:"11px 14px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div><div style={{fontWeight:700,fontSize:13}}>vs {m.opponent||"TBC"}</div><div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>{fmtDate(m.date)}{m.kickoff?` · ${m.kickoff}`:""}{m.venue?` · ${m.venue}`:""}</div></div>
                    <span style={{fontSize:11,color:"var(--c-muted2)"}}>→</span>
                  </div>
                ))}
              </div>
            </div>}
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Results</div>
              {playedMatches.length===0?(
                <div style={{...C,padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No matches played yet.</div>
              ):(
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",gap:9}}>
                  {playedMatches.map(m=>{
                    const res=m.our_score>m.opp_score?"W":m.our_score<m.opp_score?"L":"D";
                    const resColor=res==="W"?"#10b981":res==="L"?"#ef4444":"#f59e0b";
                    return(
                      <div key={m.id} onClick={()=>{setDetailMatch(m);setMatchTab("result");}} style={{...C,padding:"11px 14px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div><div style={{fontWeight:700,fontSize:13}}>vs {m.opponent||"TBC"}</div><div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>{fmtDate(m.date)}</div></div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontWeight:800,fontSize:14}}>{m.our_score}–{m.opp_score}</span>
                          <span style={{width:20,height:20,borderRadius:"50%",background:resColor,color:"#fff",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center"}}>{res}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </>):(
              [...upcoming,...playedMatches].length===0?(
                <div style={{...C,padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No fixtures yet.</div>
              ):(
                <div style={{...C,padding:14}}>
                  <MatchCalendarView ac={ac} AS={AS} items={[...upcoming,...playedMatches].map(m=>{
                    const res=m.our_score>m.opp_score?"W":m.our_score<m.opp_score?"L":"D";
                    const resColor=res==="W"?"#10b981":res==="L"?"#ef4444":"#f59e0b";
                    return{id:m.id,date:m.date,title:`vs ${m.opponent||"TBC"}`,subtitle:m.kickoff?`⏱ ${m.kickoff}`:(m.venue||""),resultLabel:m.played?`${m.our_score}–${m.opp_score}`:null,resultColor:m.played?resColor:null,played:m.played};
                  })} onSelectItem={it=>{const m=[...upcoming,...playedMatches].find(x=>x.id===it.id);if(m){setDetailMatch(m);setMatchTab(m.played?"result":"lineup");}}}/>
                </div>
              )
            )}
          </div>
        )}

        {view==="matches"&&detailMatch&&(()=>{
          const hasLineup=(detailMatch.slots||[]).some(s=>s.playerId);
          const hasNotes=!!detailMatch.notes;
          const availableTabs=[["result","📊 Result"],hasLineup&&["lineup","🗂 Lineup"],hasNotes&&["notes","📋 Notes"]].filter(Boolean);
          return(
            <div>
              <button onClick={()=>setDetailMatch(null)} style={{alignSelf:"flex-start",fontSize:11,fontWeight:700,color:ac,background:"none",border:"none",cursor:"pointer",padding:0,marginBottom:12}}>← Back to Matches</button>
              <div style={{...C,padding:16}}>
                <div style={{fontSize:16,fontWeight:800}}>vs {detailMatch.opponent||"TBC"}</div>
                {/* Meeting time / kick-off / date / location, big and clearly labelled
                    rather than crammed into one small grey caption line. */}
                <div style={{marginBottom:14}}><MatchKeyInfo match={detailMatch} ac={ac} compact={isMobile}/></div>
                {availableTabs.length>1&&<div style={{display:"flex",gap:4,marginBottom:14,background:AS.bgColor,borderRadius:8,padding:4}}>
                  {availableTabs.map(([v,l])=>(<button key={v} onClick={()=>setMatchTab(v)} style={{flex:1,padding:"7px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:matchTab===v?ac:"transparent",color:matchTab===v?"#fff":"var(--c-muted)"}}>{l}</button>))}
                </div>}
                {matchTab==="result"&&(detailMatch.played?(
                  <div style={{background:AS.bgColor,borderRadius:10,padding:14,textAlign:"center"}}>
                    <div style={{fontSize:9,color:"var(--c-text)",marginBottom:7,fontWeight:600,textTransform:"uppercase"}}>Final Score</div>
                    <div style={{fontSize:28,fontWeight:900}}>{detailMatch.our_score} – {detailMatch.opp_score}</div>
                  </div>
                ):(
                  <div style={{textAlign:"center",color:"var(--c-muted)",fontSize:13,padding:"20px 0"}}>This match hasn't been played yet.</div>
                ))}
                {matchTab==="lineup"&&hasLineup&&(
                  <ReadOnlyPitch players={players} slots={detailMatch.slots} subs={detailMatch.subs} tc={ac} strategies={teamStrategies.filter(s=>(detailMatch.attached_strategy_ids||[]).includes(s.id))} roles={[["👑 Captain",detailMatch.captain_id],["🚩 Corner Taker (Left)",detailMatch.corner_taker_left_id],["🚩 Corner Taker (Right)",detailMatch.corner_taker_right_id],["🎯 Free Kick Taker",detailMatch.free_kick_taker_id],["⚽ Penalty Taker",detailMatch.penalty_taker_id]].filter(([,pid])=>pid).map(([label,pid])=>[label,players.find(pl=>pl.id===pid)?.name||"—"])} isMobile={isMobile}/>
                )}
                {matchTab==="notes"&&hasNotes&&(
                  <div style={{fontSize:13,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{detailMatch.notes}</div>
                )}
              </div>
            </div>
          );
        })()}

        {view==="stats"&&(<>
          <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:8,padding:3,marginBottom:14,border:`1px solid ${AS.borderColor}`}}>
            {[["team","Team"],["player",child?.name?.split(" ")[0]||"Player"]].map(([v,l])=>(
              <button key={v} onClick={()=>setStatsTab(v)} style={{flex:1,padding:"8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:statsTab===v?ac:"transparent",color:statsTab===v?"#fff":"var(--c-muted)"}}>{l}</button>
            ))}
          </div>
          {statsTab==="team"&&(()=>{
            const wins=playedMatches.filter(f=>f.our_score>f.opp_score).length;
            const draws=playedMatches.filter(f=>f.our_score===f.opp_score).length;
            const losses=playedMatches.filter(f=>f.our_score<f.opp_score).length;
            const cleanSheets=playedMatches.filter(f=>f.opp_score===0).length;
            const totalGoals=players.reduce((a,p)=>a+(p.goals||0),0);
            const totalAssists=players.reduce((a,p)=>a+(p.assists||0),0);
            const teamStatOptions=[{id:"quickStats",label:"Quick Stats"},{id:"resultsBreakdown",label:"Results Breakdown"},{id:"homeAway",label:"Home vs Away"},{id:"matchResults",label:"Match Results"},{id:"topScorers",label:"Top Scorers"},{id:"topAssisters",label:"Top Assisters"},{id:"squadAvailability",label:"Squad Availability"},featAchievements&&{id:"teamAchievements",label:"Team Achievements"}].filter(Boolean);
            return(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"flex",justifyContent:"flex-end"}}>
                <StatsFilterMenu options={teamStatOptions} visible={teamStatsVisible} setVisible={setTeamStatsVisible} AS={AS}/>
              </div>
              {teamStatsVisible.has("quickStats")&&<div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:9}}>
                {[["Players",players.length,ac],["Top Scorer",topScorer?`${topScorer.name.split(" ")[0]} (${topScorer.goals||0})`:"—","#f59e0b"]].map(([l,v,c])=>(
                  <div key={l} style={{...C,padding:14,textAlign:"center"}}><div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700,marginBottom:4}}>{l}</div><div style={{fontSize:v.toString().length>8?14:20,fontWeight:800,color:c}}>{v}</div></div>
                ))}
              </div>}
              {teamStatsVisible.has("resultsBreakdown")&&<div style={{...C,padding:16}}>
                <h3 style={{margin:"0 0 12px",fontSize:10,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Results Breakdown</h3>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:9}}>
                  {[["Wins",wins,"#22c55e"],["Draws",draws,"#f59e0b"],["Losses",losses,"#ef4444"],["Clean Sheets",cleanSheets,ac]].map(([l,v,c])=>(
                    <div key={l} style={{background:AS.bgColor,borderRadius:8,padding:"11px 6px",textAlign:"center"}}>
                      <div style={{fontSize:18,fontWeight:700,color:c}}>{v}</div>
                      <div style={{fontSize:9,color:"var(--c-muted)",marginTop:2}}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>}
              {teamStatsVisible.has("homeAway")&&<HomeAwaySplitCard matches={playedMatches} AS={AS}/>}
              {teamStatsVisible.has("matchResults")&&<div>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Match Results</div>
                {playedMatches.length===0?(
                  <div style={{...C,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No matches played yet.</div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {playedMatches.map(m=>{
                      const res=m.our_score>m.opp_score?"W":m.our_score<m.opp_score?"L":"D";
                      const resColor=res==="W"?"#10b981":res==="L"?"#ef4444":"#f59e0b";
                      return(<div key={m.id} style={{...C,padding:"9px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:12,fontWeight:600}}>vs {m.opponent||"TBC"}</span>
                        <span style={{display:"flex",alignItems:"center",gap:7}}><span style={{fontWeight:700,fontSize:12}}>{m.our_score}–{m.opp_score}</span><span style={{width:16,height:16,borderRadius:"50%",background:resColor,color:"#fff",fontSize:8,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center"}}>{res}</span></span>
                      </div>);
                    })}
                  </div>
                )}
              </div>}
              {teamStatsVisible.has("topScorers")&&<div style={{...C,padding:16}}>
                <h3 style={{margin:"0 0 12px",fontSize:10,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Top Scorers</h3>
                {players.length===0?<div style={{color:"var(--c-muted2)",fontSize:11}}>No players yet.</div>:[...players].sort((a,b)=>(b.goals||0)-(a.goals||0)).slice(0,5).map((p,i)=>(
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:9,marginBottom:8}}>
                    <span style={{width:14,color:i===0?"#f59e0b":"var(--c-muted2)",fontWeight:700,fontSize:11}}>{i+1}</span>
                    <span style={{flex:1,fontWeight:600,fontSize:11}}><Flag value={p.nationality}/> {p.name}</span>
                    <span style={{fontSize:9,color:"var(--c-muted)",marginRight:3}}>{p.position}</span>
                    <div style={{width:65,background:AS.bgColor,borderRadius:3,height:4,overflow:"hidden"}}><div style={{width:`${totalGoals?((p.goals||0)/totalGoals)*100:0}%`,background:ac,height:"100%",borderRadius:3}}/></div>
                    <span style={{fontWeight:700,width:18,textAlign:"right",fontSize:11}}>{p.goals||0}</span>
                  </div>
                ))}
              </div>}
              {teamStatsVisible.has("topAssisters")&&<div style={{...C,padding:16}}>
                <h3 style={{margin:"0 0 12px",fontSize:10,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Top Assisters</h3>
                {players.length===0?<div style={{color:"var(--c-muted2)",fontSize:11}}>No players yet.</div>:[...players].sort((a,b)=>(b.assists||0)-(a.assists||0)).slice(0,5).map((p,i)=>(
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:9,marginBottom:8}}>
                    <span style={{width:14,color:i===0?"#f59e0b":"var(--c-muted2)",fontWeight:700,fontSize:11}}>{i+1}</span>
                    <span style={{flex:1,fontWeight:600,fontSize:11}}><Flag value={p.nationality}/> {p.name}</span>
                    <span style={{fontSize:9,color:"var(--c-muted)",marginRight:3}}>{p.position}</span>
                    <div style={{width:65,background:AS.bgColor,borderRadius:3,height:4,overflow:"hidden"}}><div style={{width:`${totalAssists?((p.assists||0)/totalAssists)*100:0}%`,background:"#8b5cf6",height:"100%",borderRadius:3}}/></div>
                    <span style={{fontWeight:700,width:18,textAlign:"right",fontSize:11}}>{p.assists||0}</span>
                  </div>
                ))}
              </div>}
              {teamStatsVisible.has("squadAvailability")&&<div style={{...C,padding:16}}>
                <h3 style={{margin:"0 0 12px",fontSize:10,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Squad Availability</h3>
                <div style={{display:"flex",gap:9,flexWrap:"wrap"}}>{STATUS_OPTIONS.map(s=>{const count=players.filter(p=>p.status===s).length;return(<div key={s} style={{flex:1,minWidth:75,background:AS.bgColor,borderRadius:8,padding:"11px",border:`1px solid ${STATUS_COLORS[s].dot}33`}}><div style={{fontSize:17,fontWeight:700,color:STATUS_COLORS[s].dot}}>{count}</div><div style={{fontSize:9,color:"var(--c-muted)",marginTop:1}}>{s}</div></div>);})}</div>
              </div>}
              {featAchievements&&teamStatsVisible.has("teamAchievements")&&(
              <div style={{...C,padding:16}}>
                <h3 style={{margin:"0 0 12px",fontSize:10,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>🏅 Team Achievements</h3>
                <AchievementBadges rows={teamAchievementRows} emptyText="No team achievements yet."/>
              </div>
              )}
            </div>
            );
          })()}
          {statsTab==="player"&&child&&(
            <CoachPlayerStatsView player={players.find(p=>p.id===child.id)||child} team={team} sessions={sessions} fixtures={fixtures} token={auth.token} coachId={auth.viewer.id} ac={ac} AS={AS} onBack={()=>setStatsTab("team")} onPlayerPhotoUpdated={onPlayerPhotoUpdated} featAchievements={featAchievements} featPlayerDev={featPlayerDev} featPlayerRatings={featPlayerRatings} featMatchDay={featMatchDay}/>
          )}
        </>)}

        {view==="chat"&&(
          <div style={{...C,padding:16,height:isMobile?"calc(100vh - 230px)":560,display:"flex",flexDirection:"column"}}>
            <div style={{flexShrink:0,marginBottom:10,paddingBottom:10,borderBottom:`1px solid ${AS.borderColor}`}}>
              <h3 style={{margin:"0 0 4px",fontSize:13,fontWeight:800,color:"var(--c-text)"}}>💬 Chat with {team?.name?`${team.name}'s Coach`:"the Coach"}</h3>
              <div style={{fontSize:11,color:"var(--c-muted)",lineHeight:1.5}}>{(team?.parent_chat_mode||"twoway")==="twoway"?"A shared space for questions, updates, and anything else about your child's team — every parent on the team and the coaching staff can see and post here.":"Updates from the coaching staff about your child's team — this channel is read-only for parents, so you won't be able to post here."}</div>
            </div>
            <div style={{flex:1,minHeight:0}}>
              <TeamChatView teamId={team?.id} channel="parent" senderType="parent" senderId={auth.viewer.id} senderName={auth.viewer.name} token={auth.token} ac={ac} AS={AS} canPost={(team?.parent_chat_mode||"twoway")==="twoway"}/>
            </div>
          </div>
        )}
        {view==="leagues"&&featLeagues&&(
          <PlayerLeaguesView teamId={team?.id} token={auth.token} ac={ac} AS={AS}/>
        )}
        {view==="payments"&&featPayments&&paymentsVisible&&(
          <MyPaymentsView teamId={team?.id} playerId={child?.id} token={auth.token} ac={ac} AS={AS} org={orgBilling} accountRole="parent"/>
        )}
        {view==="notifications"&&(
          <NotificationSettingsPanel token={auth.token} userId={auth.viewer.id} ac={ac} AS={AS}/>
        )}
        {view==="accessibility"&&(
          <AccessibilitySettingsPanel ac={ac} AS={AS}/>
        )}
      </div>
      </div>

      {/* Mobile bottom nav — matches the coach/player apps' mobile navigation instead of
          the old wrapping tab row up top, which got cramped with this many tabs. Also
          used on tablet widths (e.g. iPad) for the same reason. */}
      {isBottomNav&&<nav style={{position:"fixed",bottom:0,left:0,right:0,background:AS.surfaceColor,borderTop:`1px solid ${AS.borderColor}`,display:"flex",zIndex:200,paddingBottom:"env(safe-area-inset-bottom,0px)"}}>
        {navTabs.map(([v,icon,label])=>(
          <button key={v} onClick={()=>{setView(v);setDetailMatch(null);}} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"7px 0",background:"none",border:"none",cursor:"pointer",color:view===v?ac:"var(--c-muted)",borderTop:view===v?`2px solid ${ac}`:"2px solid transparent"}}>
            <Icon name={icon} size={18}/>
            <span style={{fontSize:9,fontWeight:view===v?700:400}}>{label}</span>
          </button>
        ))}
      </nav>}

      {/* Back button reached the very first page with nowhere left to go — confirm before
          ever signing anyone out, rather than doing it silently. */}
      {showLogoutConfirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:14}}>
          <div style={{background:AS.surfaceColor,borderRadius:13,border:"1px solid #7f1d1d55",width:"100%",maxWidth:340,padding:18}}>
            <div style={{fontSize:14,fontWeight:800,marginBottom:6}}>Log out?</div>
            <div style={{fontSize:12,color:"var(--c-muted)",lineHeight:1.5,marginBottom:16}}>You've gone back as far as this app can go. Do you want to log out?</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{setShowLogoutConfirm(false);onSignOut();}} style={{flex:1,padding:"9px",borderRadius:7,border:"none",background:"#ef4444",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>Yes, Log Out</button>
              <button onClick={()=>setShowLogoutConfirm(false)} style={{padding:"9px 14px",borderRadius:7,border:"1px solid var(--c-border)",background:"none",color:"var(--c-text2)",fontSize:12,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

